<template>
  <v-row>
    <v-col cols="12">
      <v-form ref="crudForm" @submit.prevent="submit">
        <v-row>
          <v-col cols="12">
            <v-text-field label="Title"
                          v-model="model.Title"
                          :rules="rules.Title" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-textarea label="Description"
                        v-model="model.Description" />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12">
            <v-row>
              <v-col cols="12">
                <v-row>
                  <v-col cols="12">
                    <h2>Weekdays</h2>
                  </v-col>
                </v-row>
                <v-expansion-panels multiple>
                  <v-expansion-panel v-for="(day, i) in weekdays"
                                     :key="i+1">
                    <v-expansion-panel-header>
                      {{ day }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <Repeater id="weekdays"
                                add-new-label="Add New Time Range"
                                v-model="model.Weekdays[day]"
                                :model="getScheduleDayModel(i + 1)">
                        <template v-slot:item="{ item }">
                          <v-col cols="12">
                            <v-checkbox v-model="item.Closed"
                                        label="Closed?"></v-checkbox>
                            <v-row>
                              <v-col md="5" cols="12">
                                <TimePicker v-model="item.Start"
                                            label="Start Time"></TimePicker>
                              </v-col>
                              <v-col md="5" cols="12">
                                <TimePicker v-model="item.End"
                                            label="End Time"></TimePicker>
                              </v-col>
                            </v-row>
                          </v-col>
                        </template>
                      </Repeater>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
              <v-col cols="12">
                <v-row>
                  <v-col cols="12">
                    <h2>Holidays</h2>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-autocomplete multiple chips
                                    deletable-chips
                                    return-object
                                    label="Holidays"
                                    ref="holidaysMenu"
                                    v-model="holidays.selected"
                                    :loading="holidays.loading"
                                    :items="holidays.items"
                                    @keydown.enter.native.prevent="">
                      <template v-slot:append>
                        <v-btn color="accent" text small>
                          SELECT <v-icon dense right>add</v-icon>
                        </v-btn>
                      </template>
                      <template v-slot:selection="data">
                        <v-chip close
                                v-bind="data.attrs"
                                @click:close="removeHoliday(data.item)">
                          {{ data.item.text }}
                        </v-chip>
                      </template>
                      <template v-slot:prepend-item>
                        <v-list-item v-if="$store.getters.can('Admin')"
                                     ripple
                                     @click="$refs.HolidayForm.open()">
                          <v-list-item-icon>
                            <v-icon>add</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            Create New...
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider />
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-expansion-panels multiple>
                      <v-expansion-panel v-for="day in holidays.selected"
                                         :key="day.value">
                        <v-expansion-panel-header>
                          {{ day.text }}
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <Repeater id="holidays"
                                    add-new-label="Add New Time Range"
                                    v-model="model.Holidays[day.text]"
                                    :model="getScheduleDayModel(day.value)">
                            <template v-slot:item="{ item }">
                              <v-col cols="12">
                                <v-checkbox v-model="item.Closed"
                                            label="Closed?"></v-checkbox>
                                <v-row>
                                  <v-col md="5" cols="12">
                                    <TimePicker v-model="item.Start"
                                                label="Start Time"></TimePicker>
                                  </v-col>
                                  <v-col md="5" cols="12">
                                    <TimePicker v-model="item.End"
                                                label="End Time"></TimePicker>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </template>
                          </Repeater>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn type="submit" color="primary" :loading="loading">
              {{ id ? 'Save' : 'Create' }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
      <NestedForm form="DayForm"
                  title="Create Holiday"
                  ref="HolidayForm"
                  v-if="$store.getters.can('Admin')"
                  @update="addNewHoliday" />
    </v-col>
  </v-row>
</template>

<script>
import FormMixins from '@/components/Mixins/Forms';
import { rules } from '@/util/Helpers';

export default {
  name: 'ScheduleForm',
  mixins: [FormMixins],
  props: {
    Id: [String, Number],
    Title: String,
    Description: String,
    Weekdays: {
      type: Object,
      default: () => ({}),
    },
    Holidays: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      id: this.Id,
      model: {
        Title: this.Title,
        Description: this.Description,
        Weekdays: this.Weekdays,
        Holidays: this.Holidays,
      },
      rules: {
        Title: [
          (v) => rules.required(v, 'Please enter a Schedule Name'),
        ],
      },
      loading: false,
      holidays: {
        loading: true,
        items: [],
        selected: Object.keys(this.Holidays).map((k) => ({
          text: k,
          // These are grouped, so the first record in each set is fine
          value: this.Holidays[k][0].DayId,
        })),
      },
      weekdays: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ],
    };
  },
  methods: {
    getScheduleDayModel(id = null) {
      return {
        DayId: id,
        Closed: false,
        Start: null,
        End: null,
      };
    },
    addNewHoliday(response) {
      const newItem = {
        text: response.Title,
        value: response.Id,
      };
      this.holidays.items.push(newItem);
      this.holidays.selected.push(newItem);
    },
    removeHoliday(item) {
      const i = this.holidays.selected.findIndex((h) => h.value === item.value);
      this.$delete(this.holidays.selected, i);
      this.$delete(this.model.Holidays, item.text);
    },
    submit() {
      if (this.$refs.crudForm.validate()) {
        this.loading = true;
        if (this.id) {
          this.update();
        } else {
          this.create();
        }
        this.loading = false;
      } else {
        this.$refs.crudForm.inputs.every(this.goToFirstError);
      }
    },
    async create() {
      try {
        const response = await this.$store.dispatch('post', {
          api: 'Schedule',
          data: {
            Title: this.model.Title,
            Description: this.model.Description,
            Weekdays: Object.values(this.model.Weekdays).flat(),
            Holidays: Object.values(this.model.Holidays).flat(),
          },
        });
        this.$emit('snackbar', 'Schedule successfully created!');
        this.$emit('crud:create', response);
        this.$emit('crud:success', response);
        this.id = response.data.Id;
      } catch (e) {
        this.$emit('crud:error', e);
      }
    },
    async update() {
      try {
        const response = await this.$store.dispatch('put', {
          api: `Schedule/${this.id}`,
          data: {
            Title: this.model.Title,
            Description: this.model.Description,
            Weekdays: Object.values(this.model.Weekdays).flat(),
            Holidays: Object.values(this.model.Holidays).flat(),
          },
        });
        this.$emit('snackbar', 'Schedule successfully updated!');
        this.$emit('crud:update', response);
        this.$emit('crud:success', response);
      } catch (e) {
        this.$emit('crud:error', e);
      }
    },
  },
  created() {
    this.$store.dispatch('get', 'Day').then((response) => {
      this.holidays.items = response.data.map((d) => ({
        text: d.Title,
        value: d.Id,
      }));
    }).finally(() => {
      this.holidays.loading = false;
    });
  },
};
</script>
